import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ShieldCheck, Sparkles, HeartHandshake, Clock } from "lucide-react";
import { Reveal, RevealImage, MaskedLines, Counter, Marquee } from "@/lib/motion";
import { Page, Overline, PrimaryButton, CTABand } from "@/components/shared";
import { STATS, MARQUEE } from "@/lib/site";
import { useContent, useFeaturedDoctor, buildWa } from "@/lib/ContentProvider";

const EASE = [0.76, 0, 0.24, 1];

function Hero() {
  const { siteConfig, hero } = useContent();
  const doctor = useFeaturedDoctor();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const headlineLines = (hero?.headline && hero.headline.length > 0)
    ? hero.headline
    : ["Expert Laser &", <span key="l2">Laparoscopic <span className="italic text-alch-terracotta">Care</span></span>, "in Haldwani"];
  const subtitle = hero?.subtitle || "Minimally invasive, precision-driven surgical care. Trusted for the best piles, varicose veins, fissure, and laparoscopic treatments — with fast recovery and truly affordable cost.";

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden bg-alch-bg px-5 pt-36 md:px-8 md:pt-44">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-alch-terracotta" />
              <Overline>{siteConfig.tagline}</Overline>
            </div>
          </Reveal>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={headlineLines}
            className="mt-6 font-serif text-5xl leading-[1.02] tracking-tight text-alch-forest md:text-7xl lg:text-[5.5rem]"
          />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-alch-ink/80">
              {subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <PrimaryButton href={buildWa(siteConfig.whatsapp)} data-testid="hero-book">Book Appointment</PrimaryButton>
              <a href={`tel:${siteConfig.phoneRaw}`} data-testid="hero-call"
                className="rounded-full border border-alch-forest/25 px-7 py-4 text-sm font-medium text-alch-forest transition-colors hover:bg-alch-forest hover:text-alch-bg">
                Call Now
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.4} className="relative">
          <div className="relative mx-auto max-w-md overflow-hidden arch-frame shadow-[0_30px_60px_rgba(36,62,54,0.18)]">
            <motion.img
              style={{ y, scale }}
              src={doctor.image}
              alt={doctor.name}
              className="h-[30rem] w-full object-cover md:h-[34rem]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-alch-forest/30 to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: EASE }}
            className="absolute -bottom-4 left-2 rounded-2xl border border-alch-forest/10 bg-alch-bg/90 px-5 py-4 backdrop-blur-md md:left-0"
          >
            <p className="font-serif text-3xl text-alch-forest">5000+</p>
            <p className="text-xs uppercase tracking-[0.14em] text-alch-muted">Happy Patients</p>
          </motion.div>
        </Reveal>
      </div>

      <Reveal delay={0.7} className="mx-auto mt-16 max-w-7xl border-t border-alch-forest/10 pt-6">
        <p className="text-xs uppercase tracking-[0.2em] text-alch-muted">
          Laser Piles · Varicose Veins EVLT · Laparoscopic Surgery · Day Care Procedures · Advanced Technology
        </p>
      </Reveal>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats-bar" className="border-y border-alch-forest/10 bg-alch-surface px-5 py-14 md:px-8">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center md:text-left">
            <p className="font-serif text-5xl text-alch-forest md:text-6xl">
              <Counter value={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-sm text-alch-muted">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function MarqueeBand() {
  return (
    <div className="border-b border-alch-forest/10 bg-alch-forest py-6 text-alch-bg">
      <Marquee items={MARQUEE} className="font-serif text-3xl md:text-5xl italic" />
    </div>
  );
}

function About() {
  const { siteConfig, features } = useContent();
  return (
    <section data-testid="about-home" className="px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <Overline>Welcome to ALCH</Overline>
            <Reveal>
              <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
                A dedicated centre for modern, minimally invasive surgical care in Kumaon
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="text-lg leading-relaxed text-alch-ink/80">
              Since <strong>{siteConfig.since}</strong>, we have focused on delivering precise, patient-friendly
              treatments using advanced laser and laparoscopic technology. Under the leadership of renowned
              surgeon <strong>Dr. Pramod Kumar Karan</strong>, we are widely trusted for the best piles and
              varicose vein treatment in Haldwani, and advanced day-care laser procedures — among the first
              and most advanced laser centres in the region.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <Reveal key={f.no || f.title} delay={i * 0.08}>
              <div className="group h-full rounded-[1.5rem] border border-alch-forest/10 bg-alch-surface p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(36,62,54,0.08)]">
                <span className="font-serif text-3xl text-alch-terracotta">{f.no || `0${i + 1}`}</span>
                <h3 className="mt-5 font-serif text-2xl leading-snug text-alch-forest">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-alch-ink/70">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { services } = useContent();
  return (
    <section data-testid="services-home" className="bg-alch-surface px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <Overline>Our Medical Services</Overline>
          <Reveal>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
              Comprehensive minimally invasive treatments under one roof
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {services.map((s, i) => (
            <Reveal key={s.key || s.title} delay={i * 0.08}>
              <article className={`group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-alch-forest/10 bg-alch-bg ${i % 3 === 0 ? "md:flex-row" : ""}`}>
                <RevealImage
                  src={s.image}
                  alt={s.title}
                  className={`${i % 3 === 0 ? "md:w-2/5" : "h-52"}`}
                  imgClassName="transition-transform duration-700 group-hover:scale-105 h-full w-full"
                />
                <div className="flex-1 p-8">
                  <h3 className="font-serif text-2xl text-alch-forest">{s.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm text-alch-ink/75">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-alch-terracotta" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY_ICONS = [Sparkles, Sparkles, ShieldCheck, HeartHandshake, HeartHandshake, Clock];

function Why() {
  const { why } = useContent();
  return (
    <section data-testid="why-home" className="px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-end gap-6 md:grid-cols-2">
          <div>
            <Overline>Why patients choose ALCH</Overline>
            <Reveal>
              <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
                Safe, precise & affordable surgical care
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-alch-ink/75 md:text-right">
              Thousands of patients across Kumaon trust us for outcomes that combine world-class technology
              with genuine, human care.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px overflow-hidden rounded-[1.5rem] border border-alch-forest/12 bg-alch-forest/12 md:grid-cols-3">
          {why.map((w, i) => {
            const Icon = WHY_ICONS[i] || Sparkles;
            return (
              <Reveal key={w.title} delay={(i % 3) * 0.08} className="bg-alch-bg">
                <div className="group h-full p-9 transition-colors duration-500 hover:bg-alch-surface">
                  <Icon className="h-7 w-7 text-alch-terracotta" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-5 font-serif text-2xl text-alch-forest">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-alch-ink/70">{w.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 rounded-[1.5rem] bg-alch-forest px-8 py-8 text-center text-alch-bg">
          <span className="font-serif text-2xl">5K+ <span className="text-sm font-sans text-alch-bg/60">Patients Treated</span></span>
          <span className="font-serif text-2xl">100% <span className="text-sm font-sans text-alch-bg/60">Minimally Invasive</span></span>
          <span className="font-serif text-2xl">20+ <span className="text-sm font-sans text-alch-bg/60">Treatments</span></span>
        </Reveal>
      </div>
    </section>
  );
}

function Mission() {
  const { mission } = useContent();
  const doctor = useFeaturedDoctor();
  return (
    <section data-testid="mission-home" className="bg-alch-forest px-5 py-24 text-alch-bg md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <Overline>Our Mission</Overline>
          <Reveal>
            <p className="mt-6 font-serif text-3xl leading-snug md:text-[2.6rem]">
              To deliver safe, effective, and affordable advanced laser and laparoscopic treatments while
              maintaining the highest standards of surgical excellence — making world-class care accessible to all.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {mission.map((m, i) => (
            <Reveal key={m.no || m.title} delay={(i % 2) * 0.1}>
              <div className="flex gap-6 border-t border-alch-bg/15 pt-8">
                <span className="font-serif text-5xl leading-none text-alch-terracotta">{m.no || `0${i + 1}`}</span>
                <div>
                  <h3 className="font-serif text-2xl">{m.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-alch-bg/70">{m.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border-l-2 border-alch-terracotta pl-8">
          <p className="font-serif text-2xl italic leading-relaxed md:text-3xl">
            &ldquo;{doctor.quote}&rdquo;
          </p>
          <p className="mt-4 text-sm uppercase tracking-[0.16em] text-alch-terracotta">— {doctor.name}</p>
        </Reveal>
      </div>
    </section>
  );
}

function DoctorSpotlight() {
  const doctor = useFeaturedDoctor();
  return (
    <section data-testid="doctor-home" className="px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.8fr_1.2fr]">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem]">
            <RevealImage src={doctor.image} alt={doctor.name} className="h-[28rem]" />
            <div className="absolute inset-0 bg-gradient-to-t from-alch-forest/40 to-transparent" />
          </div>
        </Reveal>
        <div>
          <Overline>Meet the surgeon</Overline>
          <Reveal>
            <h2 className="mt-5 font-serif text-4xl text-alch-forest md:text-6xl">{doctor.name}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-3 text-sm uppercase tracking-[0.14em] text-alch-terracotta">{doctor.role}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 leading-relaxed text-alch-ink/80">{doctor.bio}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-2">
              {(doctor.expertise || []).map((e) => (
                <span key={e} className="rounded-full border border-alch-forest/20 px-4 py-1.5 text-xs text-alch-forest">{e}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex gap-10">
              {(doctor.stats || []).map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-4xl text-alch-forest">{s.value}</p>
                  <p className="text-xs uppercase tracking-[0.12em] text-alch-muted">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Awards() {
  const { awards } = useContent();
  return (
    <section data-testid="awards-home" className="bg-alch-surface px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <Overline>Awards & Recognitions</Overline>
          <Reveal>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
              Recognised for excellence & affordable care across Uttarakhand
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {awards.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="h-full rounded-[1.5rem] border border-alch-forest/10 bg-alch-bg p-8">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-alch-forest text-alch-bg">
                  <Star className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-serif text-xl leading-snug text-alch-forest">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-alch-ink/70">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { testimonials } = useContent();
  return (
    <section data-testid="testimonials-home" className="px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <Overline>Patient Stories</Overline>
          <Reveal>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
              Real recoveries, in their own words
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 columns-1 gap-6 md:columns-2 lg:columns-3">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="mb-6 break-inside-avoid">
              <figure className="rounded-[1.5rem] border border-alch-forest/10 bg-alch-surface p-7">
                <div className="flex gap-1">
                  {Array.from({ length: t.stars }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-alch-terracotta text-alch-terracotta" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-alch-ink/80">&ldquo;{t.text}&rdquo;</blockquote>
                <figcaption className="mt-5 border-t border-alch-forest/10 pt-4">
                  <span className="font-serif text-lg text-alch-forest">{t.name}</span>
                  <span className="ml-2 text-xs text-alch-muted">{t.place}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <Page testid="home-page">
      <Hero />
      <Stats />
      <MarqueeBand />
      <About />
      <Services />
      <Why />
      <Mission />
      <DoctorSpotlight />
      <Awards />
      <Testimonials />
      <CTABand />
    </Page>
  );
}
